// pages/Agencias.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  InputNumber,
  Select,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Popconfirm,
  Badge,
  Statistic
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BlockOutlined,
  UnlockOutlined,
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  GlobalOutlined,
  ExportOutlined
} from '@ant-design/icons';
import axios from 'axios';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { Option } = Select;

const AgenciasViajes = () => {
  // Estados
  const [agencias, setAgencias] = useState([]);
  const [agenciasFiltradas, setAgenciasFiltradas] = useState([]);
  const [paises, setPaises] = useState([]);
  const [series, setSeries] = useState([]);
  const [idiomas, setIdiomas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [currentAgencia, setCurrentAgencia] = useState(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const searchInput = useRef(null);
  
  // Estados para filtros y ordenación
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});

  const renderFlag = (codigoISO) => {
    if (!codigoISO || codigoISO.trim() === '') {
      return <span style={{ marginRight: '8px' }}>🌍</span>;
    }
    return <span className={`fi fi-${codigoISO.toLowerCase()}`} style={{ marginRight: '8px' }} />;
  };

  // Función de búsqueda en tiempo real
  const handleSearch = (value) => {
    setSearchText(value);
    
    if (!value.trim()) {
      setAgenciasFiltradas(agencias);
      return;
    }

    const searchLower = value.toLowerCase().trim();
    const filtered = agencias.filter(agencia => {
      // Buscar en los campos especificados
      const codigo = agencia.Codigo?.toString().toLowerCase() || '';
      const nombre = agencia.Nombre?.toLowerCase() || '';
      const telefono = agencia.Telefono1?.toLowerCase() || '';
      const email = agencia.Email?.toLowerCase() || '';
      
      return codigo.includes(searchLower) || 
             nombre.includes(searchLower) || 
             telefono.includes(searchLower) || 
             email.includes(searchLower);
    });
    
    setAgenciasFiltradas(filtered);
  };

  // Cargar datos iniciales
  useEffect(() => {
    fetchAgencias();
    fetchSeries();
    fetchIdiomas();
  }, []);

  // Actualizar agencias filtradas cuando cambia la lista principal
  useEffect(() => {
    if (searchText.trim()) {
      handleSearch(searchText);
    } else {
      setAgenciasFiltradas(agencias);
    }
  }, [agencias]);

  const fetchAgencias = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${BASE_URL}/api/agencias`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAgencias(response.data.data);
      setAgenciasFiltradas(response.data.data);

      const responsePaises = await axios.get(`${BASE_URL}/api/paises`, {
        headers: { Authorization: `Bearer ${token}` }
      });      

      setPaises(responsePaises.data.data)

    } catch (error) {
      message.error('Error al cargar las agencias');
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSeries = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/series`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log(response.data.data);
      
      setSeries(response.data.data);
    } catch (error) {
      message.error('Error al cargar las series');
      console.log(error);
    }
  };

  const fetchIdiomas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/idiomas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIdiomas(response.data.data);
    } catch (error) {
      message.error('Error al cargar los idiomas');
      console.log(error);
    }
  };

  // Handlers para las acciones
  const handleCreate = () => {
    setModalType('create');
    setCurrentAgencia(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setModalType('edit');
    setCurrentAgencia(record);
    form.setFieldsValue({
      ...record,
      DesGuia: record.DesGuia || 0,
      CodigoHotel: record.CodigoHotel || 0,
    });
    setModalVisible(true);
  };

  const handleView = (record) => {
    Modal.info({
      title: `Detalle de Agencia: ${record.Nombre}`,
      width: 800,
      content: (
        <div style={{ marginTop: 20 }}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <strong>Código:</strong> {record.Codigo}
            </Col>
            <Col span={12}>
              <strong>NIF:</strong> {record.NIF}
            </Col>
            <Col span={12}>
              <strong>Dirección:</strong> {record.Direccion}
            </Col>
            <Col span={12}>
              <strong>Dirección Correspondencia:</strong> {record.DireccionCorres}
            </Col>
            <Col span={12}>
              <strong>Población:</strong> {record.Poblacion}
            </Col>
            <Col span={12}>
              <strong>Población Correspondencia:</strong> {record.PoblacionCorres}
            </Col>
            <Col span={12}>
              <strong>Provincia:</strong> {record.Provincia}
            </Col>
            <Col span={12}>
              <strong>Teléfono:</strong> {record.Telefono1}
            </Col>
            <Col span={12}>
              <strong>Código Postal:</strong> {record.CodPostal}
            </Col>
            <Col span={12}>
              <strong>Crédito:</strong> {record.Credito ? 'SÍ' : 'NO'}
            </Col>
            <Col span={12}>
              <strong>Bloqueada:</strong> {record.Bloqueada ? 'SÍ' : 'NO'}
            </Col>
            <Col span={12}>
              <strong>Tipo Cliente:</strong> {record.TipoCliente}
            </Col>
            <Col span={12}>
              <strong>Idioma:</strong> {record.TIdioma}
            </Col>
            <Col span={12}>
              <strong>Envía Factura PDF:</strong> {record.EnviaFacturaPDF ? 'SÍ' : 'NO'}
            </Col>
            {record.Email && (
              <Col span={12}>
                <strong>Email:</strong> {record.Email}
              </Col>
            )}
          </Row>
        </div>
      ),
      onOk() {},
    });
  };

  const handleToggleBlock = async (record) => {
    try {
      const token = localStorage.getItem('token');
      const updatedAgencia = { ...record, Bloqueada: !record.Bloqueada };
      
      await axios.patch(
        `${BASE_URL}/api/agencias/${record.Codigo}/estado`,
        { Bloqueada: !record.Bloqueada },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAgencias(agencias.map(agencia => 
        agencia.Codigo === record.Codigo ? updatedAgencia : agencia
      ));
      
      message.success(`Agencia ${updatedAgencia.Bloqueada ? 'bloqueada' : 'desbloqueada'} correctamente`);
    } catch (error) {
      message.error('Error al actualizar el estado');
    }
  };

  const handleDelete = async (codigo) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BASE_URL}/api/agencias/${codigo}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAgencias(agencias.filter(agencia => agencia.Codigo !== codigo));
      message.success('Agencia eliminada correctamente');
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al eliminar la agencia');
    }
  };

  // Handler para guardar (crear o editar)
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const token = localStorage.getItem('token');
      
      if (modalType === 'create') {
        const response = await axios.post(
          `${BASE_URL}/api/agencias`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setAgencias([...agencias, response.data.data]);
        message.success('Agencia creada correctamente');
      } else {
        const response = await axios.put(
          `${BASE_URL}/api/agencias/${currentAgencia.Codigo}`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setAgencias(agencias.map(agencia => 
          agencia.Codigo === currentAgencia.Codigo ? response.data.data : agencia
        ));
        message.success('Agencia actualizada correctamente');
      }
      
      setModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al guardar los datos');
    }
  };

  // Handlers para filtros y ordenación
  const handleChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  const clearFilters = () => {
    setFilteredInfo({});
    setSortedInfo({});
    setSearchText('');
    setAgenciasFiltradas(agencias);
  };

// Función de ordenación personalizada para códigos mixtos (números y texto)
const sortCodigo = (a, b) => {
  const codigoA = a.Codigo;
  const codigoB = b.Codigo;
  
  // Convertir a número si es posible
  const numA = parseFloat(codigoA);
  const numB = parseFloat(codigoB);
  
  // Verificar si ambos son números válidos
  const isNumA = !isNaN(numA) && isFinite(numA);
  const isNumB = !isNaN(numB) && isFinite(numB);
  
  // Caso 1: Ambos son números - ordenar numéricamente
  if (isNumA && isNumB) {
    return numA - numB;
  }
  
  // Caso 2: Solo A es número - los números van primero
  if (isNumA && !isNumB) {
    return -1;
  }
  
  // Caso 3: Solo B es número - los números van primero
  if (!isNumA && isNumB) {
    return 1;
  }
  
  // Caso 4: Ambos son texto - ordenar alfabéticamente
  return codigoA.localeCompare(codigoB);
};


  // Configuración de columnas de la tabla  
  const columns = [
    {
      title: 'Código',
      dataIndex: 'Codigo',
      key: 'Codigo',
  sorter: sortCodigo, // Usar la función personalizada
  sortOrder: sortedInfo.columnKey === 'Codigo' && sortedInfo.order,
      width: 100,
    },
    {
      title: 'Nombre',
      dataIndex: 'Nombre',
      key: 'Nombre',
      sorter: (a, b) => a.Nombre.localeCompare(b.Nombre),
      sortOrder: sortedInfo.columnKey === 'Nombre' && sortedInfo.order,
      ellipsis: true,
      width: 200,
    },
    {
      title: 'NIF',
      dataIndex: 'NIF',
      key: 'NIF',
      width: 120,
    },
    {
      title: 'Teléfono',
      dataIndex: 'Telefono1',
      key: 'Telefono1',
      width: 140,
    },
    {
      title: 'Email',
      dataIndex: 'Email',
      key: 'Email',
      width: 180,
      ellipsis: true,
    },
    {
      title: 'Población',
      dataIndex: 'Poblacion',
      key: 'Poblacion',
      sorter: (a, b) => a.Poblacion?.localeCompare(b.Poblacion),
      sortOrder: sortedInfo.columnKey === 'Poblacion' && sortedInfo.order,
      width: 140,
    },
    {
      title: 'Provincia',
      dataIndex: 'Provincia',
      key: 'Provincia',
      width: 120,
    },
    {
      title: 'Crédito',
      dataIndex: 'Credito',
      key: 'Credito',
      render: (credito) => (
        <Tag color={credito ? 'green' : 'red'}>
          {credito ? 'SÍ' : 'NO'}
        </Tag>
      ),
      width: 100,
      filters: [
        { text: 'Con Crédito', value: true },
        { text: 'Sin Crédito', value: false },
      ],
      filteredValue: filteredInfo.Credito || null,
      onFilter: (value, record) => record.Credito === value,
    },
    {
      title: 'Bloqueada',
      dataIndex: 'Bloqueada',
      key: 'Bloqueada',
      render: (bloqueada, record) => (
        <Badge
          status={bloqueada ? 'error' : 'success'}
          text={bloqueada ? 'Bloqueada' : 'Activa'}
        />
      ),
      width: 120,
      filters: [
        { text: 'Activas', value: false },
        { text: 'Bloqueadas', value: true },
      ],
      filteredValue: filteredInfo.Bloqueada || null,
      onFilter: (value, record) => record.Bloqueada === value,
    },
    {
      title: 'Tipo Cliente',
      dataIndex: 'TipoCliente',
      key: 'TipoCliente',
      width: 120,
      filters: [
        { text: 'GRUP', value: 'GRUP' },
        { text: 'INDIV', value: 'INDIV' },
        { text: 'OTRO', value: 'OTRO' },
      ],
      filteredValue: filteredInfo.TipoCliente || null,
      onFilter: (value, record) => record.TipoCliente === value,
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Tooltip title="Editar">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          
          <Tooltip title={record.Bloqueada ? "Desbloquear" : "Bloquear"}>
            <Button
              type={record.Bloqueada ? "primary" : "dashed"}
              danger={!record.Bloqueada}
              icon={record.Bloqueada ? <UnlockOutlined /> : <BlockOutlined />}
              size="small"
              onClick={() => handleToggleBlock(record)}
            />
          </Tooltip>
          
          <Popconfirm
            title="¿Eliminar agencia?"
            description="Esta acción no se puede deshacer"
            onConfirm={() => handleDelete(record.Codigo)}
            okText="Sí"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Eliminar">
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
          
          <Tooltip title="Ver detalle">
            <Button
              type="text"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleView(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Función para exportar (placeholder)
  const handleExport = () => {
    message.info('Función de exportación en desarrollo');
  };

  // Estadísticas
  const estadisticas = (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col span={6}>
        <Statistic 
          title="Total Agencias" 
          value={agencias.length}
          prefix={<GlobalOutlined />}
          valueStyle={{ color: '#1890ff' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Agencias Activas" 
          value={agencias.filter(a => !a.Bloqueada).length}
          valueStyle={{ color: '#52c41a' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Con Crédito" 
          value={agencias.filter(a => a.Credito).length}
          valueStyle={{ color: '#faad14' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Bloqueadas" 
          value={agencias.filter(a => a.Bloqueada).length}
          valueStyle={{ color: '#f5222d' }}
        />
      </Col>
    </Row>
  );

  return (
    <>
      <PageContainer
        title="Gestión de Agencias"
        // subtitle={`${agenciasFiltradas.length} agencias registradas`}
        icon={<GlobalOutlined />}
        loading={loading}
        onRefresh={fetchAgencias}
        onCreate={handleCreate}
        onExport={handleExport}
        // badgeCount={agencias.length}
        badgeStatus="processing"
        extra={
          <Space>
            <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px', borderRadius: 20 }}>
              <strong>{agenciasFiltradas.length}</strong> registros
            </Tag>
          </Space>
        }
        cardStyle={{
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
        }}
      >
        {/* Buscador en tiempo real */}
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Input
            placeholder="🔍 Buscar por código, nombre, teléfono o email..."
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            style={{ 
              width: 400,
              borderRadius: '8px',
              border: '1px solid #d9d9d9'
            }}
            allowClear
            size="large"
          />
          <Space>
            <Button onClick={clearFilters} icon={<ReloadOutlined />}>
              Limpiar filtros
            </Button>
          </Space>
        </div>

        {/* Estadísticas */}
        {/* {estadisticas} */}
        
        {/* Tabla */}
        <Table
          columns={columns}
          dataSource={agenciasFiltradas}
          rowKey="Codigo"
          loading={loading}
          onChange={handleChange}
          scroll={{ x: 1500 }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} de ${total} agencias`,
            pageSizeOptions: ['10', '20', '50', '100'],
            defaultPageSize: 20,
          }}
          style={{
            borderRadius: '8px',
            overflow: 'hidden'
          }}
        />
      </PageContainer>

      {/* Modal para crear/editar agencia */}
      <Modal
        title={modalType === 'create' ? 'Nueva Agencia' : 'Editar Agencia'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={800}
        okText={modalType === 'create' ? 'Crear' : 'Guardar'}
        cancelText="Cancelar"
        confirmLoading={loading}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            Credito: false,
            Bloqueada: false,
            controlHabsCup: false,
            controlHabsGar: false,
            DesGuia: 0,
            CodigoHotel: 0,
            CopiasFacturas: 1,
            TipoCuenta: 2,
            TipoCliente: 'GRUP',
            TipoAgrupa: 'H',
            TIdioma: 'Castellano',
            seActualiza: true,
            ActualizaA3: true,
            ComoFactura: 0,
          }}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="Codigo"
                label="Código"
                rules={[{ required: true, message: 'Por favor ingrese el código' }]}
              >
                <Input placeholder="Ej: 10001" disabled={modalType === 'edit'} />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item
                name="Nombre"
                label="Nombre"
                rules={[{ required: true, message: 'Por favor ingrese el nombre' }]}
              >
                <Input placeholder="Nombre de la agencia" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="NIF"
                label="NIF/CIF"
                rules={[{ required: true, message: 'Por favor ingrese el NIF' }]}
              >
                <Input placeholder="NIF de la agencia" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="Telefono1"
                label="Teléfono"
              >
                <Input placeholder="+34 123 456 789" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="Email"
                label="Email"
                rules={[{ type: 'email', message: 'Ingrese un email válido' }]}
              >
                <Input placeholder="agencia@ejemplo.com" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="TipoCliente"
                label="Tipo Cliente"
              >
                <Select>
                  <Option value="GRUP">GRUP</Option>
                  <Option value="INDIV">INDIV</Option>
                  <Option value="OTRO">OTRO</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <div style={{ margin: '20px 0', borderTop: '1px solid #f0f0f0', paddingTop: '20px' }}>
            <Title level={5} style={{ marginBottom: 16 }}>Dirección</Title>
            
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="Direccion"
                  label="Dirección"
                >
                  <Input placeholder="Dirección principal" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="DireccionCorres"
                  label="Dirección Correspondencia"
                >
                  <Input placeholder="Dirección para correspondencia" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="Poblacion"
                  label="Población"
                >
                  <Input placeholder="Población" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="PoblacionCorres"
                  label="Población Correspondencia"
                >
                  <Input placeholder="Población correspondencia" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="Provincia"
                  label="Provincia"
                >
                  <Input placeholder="Provincia" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="CodPostal"
                  label="Código Postal"
                >
                  <Input placeholder="Código postal" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="CodPostalCorres"
                  label="CP Correspondencia"
                >
                  <Input placeholder="CP correspondencia" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="PaisISO"
                  label="Pais"
                >
                  <Select
                    showSearch
                    placeholder="Seleccione pais"
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      option.label.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {paises.map(pais => (
                      <Option key={pais.Codigo} value={pais.Descripcion} label={pais.Descripcion}>
                        {renderFlag(pais.CodigoISO)}
                        {pais.Descripcion}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </div>

          <div style={{ margin: '20px 0', borderTop: '1px solid #f0f0f0', paddingTop: '20px' }}>
            <Title level={5} style={{ marginBottom: 16 }}>Configuración</Title>
            
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item
                  name="Credito"
                  label="Crédito"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="Bloqueada"
                  label="Bloqueada"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="EnviaFacturaPDF"
                  label="Envía Factura PDF"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="Serie"
                  label="Serie Facturación"
                >
                  <Select
                    showSearch
                    placeholder="Seleccione una serie"
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      option.label.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {series.map(serie => (
                      <Option 
                        key={serie.serie} 
                        value={serie.serie} 
                        label={serie.serie}
                      >
                        {serie.serie}
                        {serie.tieneproduccion && (
                          <Tag color="green" size="small" style={{ marginLeft: 8 }}>
                            Producción
                          </Tag>
                        )}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="TIdioma"
                  label="Idioma"
                >
                  <Select
                    showSearch
                    placeholder="Seleccione un idioma"
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      option.label.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {idiomas.map(idioma => (
                      <Option 
                        key={idioma.Codigo} 
                        value={idioma.Descripcion}
                        label={idioma.Descripcion}
                      >
                        {idioma.Descripcion}
                        {idioma.Pais && (
                          <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                            ({idioma.Pais})
                          </Text>
                        )}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="ComoFactura"
                  label="Nombre en Factura"
                  tooltip="Define cómo se mostrará el nombre en las facturas"
                >
                  <Select>
                    <Option value={0}>Según crédito / contado</Option>
                    <Option value={1}>Nombre de la agencia</Option>
                    <Option value={2}>Nombre del particular</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="DesGuia"
                  label="Descuento Guía (%)"
                >
                  <InputNumber
                    min={0}
                    max={100}
                    style={{ width: '100%' }}
                    step={0.01}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="CodConta"
                  label="Código Contable"
                >
                  <Input placeholder="43000032" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="CopiasFacturas"
                  label="Copias Facturas"
                >
                  <InputNumber
                    min={1}
                    max={10}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="Regimen"
                  label="Régimen"
                >
                  <Select>
                    <Option value="MP">MP</Option>
                    <Option value="PC">PC</Option>
                    <Option value="AD">AD</Option>
                    <Option value="TI">TI</Option>
                  </Select>
                </Form.Item>
              </Col>              
              <Col span={8}>
                <Form.Item
                  name="SerEntrada"
                  label="Servicio entrada"
                >
                  <Input placeholder="DE" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="SerSalida"
                  label="Servicio salida"
                >
                  <Input placeholder="HA" />
                </Form.Item>
              </Col>
            </Row>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default AgenciasViajes;